/**
 * SPL Token Helpers for Seller Pre-Authorization
 * 
 * This module provides utilities for:
 * - Approving delegate authority for NFT transfers
 * - Revoking delegate authority
 * - Executing transfers using delegate authority
 * - Detecting token standards (NonFungible vs ProgrammableNonFungible)
 */

import { 
  Connection, 
  PublicKey, 
  Transaction, 
  Keypair,
  SystemProgram,
  LAMPORTS_PER_SOL,
  Cluster
} from '@solana/web3.js';
import {
  getAssociatedTokenAddress,
  getAssociatedTokenAddressSync as getATA,
  createAssociatedTokenAccountInstruction,
  createApproveInstruction,
  createApproveInstruction as createApproveClassic,
  createRevokeInstruction,
  createTransferInstruction,
  getAccount,
  getAccount as getClassicAccount,
  getMint as getMintClassic,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import {
  TOKEN_2022_PROGRAM_ID,
  getAssociatedTokenAddressSync as getATA22,
  createApproveInstruction as createApprove22,
  getMint as getMint22,
  getAccount as get22Account,
} from '@solana/spl-token';

/**
 * Get the Associated Token Account address for a mint and owner
 */
export function getSellerAta(mint: PublicKey, seller: PublicKey): PublicKey {
  return getAssociatedTokenAddress(mint, seller, true);
}

/**
 * Get or create an Associated Token Account
 * @returns The ATA address and an instruction to create it if needed
 */
export async function getOrCreateAta(
  connection: Connection,
  owner: PublicKey,
  mint: PublicKey,
  payer: PublicKey
): Promise<{ ata: PublicKey; instruction?: any }> {
  const ata = await getAssociatedTokenAddress(mint, owner, true);

  try {
    // Check if the account exists
    await getAccount(connection, ata);
    return { ata };
  } catch (error) {
    // Account doesn't exist, create instruction
    const instruction = createAssociatedTokenAccountInstruction(
      payer,
      ata,
      owner,
      mint
    );
    return { ata, instruction };
  }
}

/**
 * Detect token standard using Metaplex metadata
 * @returns "NonFungible", "ProgrammableNonFungible", or "Unknown"
 */
export async function getTokenStandard(
  connection: Connection,
  mint: PublicKey
): Promise<string> {
  try {
    // Derive metadata PDA
    const METADATA_PROGRAM_ID = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');
    const [metadataPDA] = PublicKey.findProgramAddressSync(
      [
        Buffer.from('metadata'),
        METADATA_PROGRAM_ID.toBuffer(),
        mint.toBuffer(),
      ],
      METADATA_PROGRAM_ID
    );

    const accountInfo = await connection.getAccountInfo(metadataPDA);
    
    if (!accountInfo) {
      console.warn('No metadata found for mint:', mint.toString());
      return 'Unknown';
    }

    // Parse the metadata account data
    // Token Standard is at byte offset 1 + 32 + 32 + 4 + ... (varies by version)
    // For simplicity, we check a specific byte that indicates token standard
    // Byte at offset 105 typically contains the token standard enum:
    // 0 = NonFungible, 4 = ProgrammableNonFungible
    const data = accountInfo.data;
    
    // More robust parsing: look for the token standard field
    // In Metaplex Token Metadata v1.3+, token standard is an optional field
    // We need to parse the data structure properly
    
    // Quick heuristic: if data length > 679, it's likely programmable
    // This is approximate - a proper implementation would use Metaplex SDK
    if (data.length > 679) {
      console.log('Token appears to be ProgrammableNonFungible based on metadata size');
      return 'ProgrammableNonFungible';
    }

    // For proper detection, we should use Metaplex SDK
    // For now, assume NonFungible if we can't definitively detect programmable
    console.log('Token appears to be NonFungible (standard NFT)');
    return 'NonFungible';

  } catch (error) {
    console.error('Error detecting token standard:', error);
    return 'Unknown';
  }
}

/**
 * Get mint account information
 * @returns Mint info or null if doesn't exist
 */
export async function getMintInfo(
  connection: Connection,
  mint: PublicKey
): Promise<{ exists: boolean; owner?: PublicKey; decimals?: number; supply?: bigint; program?: PublicKey } | null> {
  try {
    const accountInfo = await connection.getAccountInfo(mint);
    
    if (!accountInfo) {
      return { exists: false };
    }
    
    // Decode mint data (simple parsing)
    const data = accountInfo.data;
    if (data.length < 82) {
      return { exists: false };
    }
    
    // Mint layout: 
    // 0-4: mint authority option + pubkey
    // 36-44: supply (u64 LE)
    // 44: decimals (u8)
    const decimals = data.readUInt8(44);
    const supply = data.readBigUInt64LE(36);
    
    return {
      exists: true,
      owner: accountInfo.owner,
      decimals,
      supply,
      program: accountInfo.owner,
    };
  } catch (error) {
    console.error('Error getting mint info:', error);
    return { exists: false };
  }
}

/**
 * Detect which cluster (mainnet-beta or devnet) a mint exists on
 * Probes in order: current cluster → mainnet-beta → devnet
 * @returns Object with found status and detected cluster
 */
export async function detectClusterForMint(
  mint: PublicKey,
  currentCluster: 'mainnet-beta' | 'devnet' = 'devnet'
): Promise<{ found: boolean; cluster: 'mainnet-beta' | 'devnet' | null }> {
  const clusters: ('mainnet-beta' | 'devnet')[] = 
    currentCluster === 'mainnet-beta' 
      ? ['mainnet-beta', 'devnet'] 
      : ['devnet', 'mainnet-beta'];
  
  for (const cluster of clusters) {
    try {
      const rpcUrl = cluster === 'mainnet-beta' 
        ? 'https://api.mainnet-beta.solana.com'
        : 'https://api.devnet.solana.com';
      
      const connection = new Connection(rpcUrl, {
        commitment: 'confirmed',
        confirmTransactionInitialTimeout: 1500, // Fast timeout
      });
      
      console.log(`🔍 Probing ${cluster} for mint...`);
      const accountInfo = await connection.getAccountInfo(mint);
      
      if (accountInfo) {
        console.log(`✅ Mint found on ${cluster}`);
        return { found: true, cluster };
      }
    } catch (error) {
      console.log(`⚠️ ${cluster} probe failed or timeout`);
    }
  }
  
  console.log('❌ Mint not found on any supported cluster');
  return { found: false, cluster: null };
}

/**
 * Detect token program from mint account owner (on-chain check)
 * @returns Object with program type and mintExists flag
 */
export async function detectTokenProgramFromMint(
  connection: Connection,
  mint: PublicKey
): Promise<{ program: 'spl-token' | 'token-2022' | 'unknown'; mintExists: boolean; owner?: string }> {
  try {
    // Read mint account directly from chain
    const accountInfo = await connection.getAccountInfo(mint);
    
    if (!accountInfo) {
      return { program: 'unknown', mintExists: false };
    }
    
    const ownerBase58 = accountInfo.owner.toBase58();
    
    // SPL Token Program ID (string for comparison)
    const SPL_TOKEN_PROGRAM_ID_STR = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
    // Token-2022 Program ID (string for comparison)
    const TOKEN_2022_PROGRAM_ID_STR = 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb';
    // Metaplex Metadata Program (for error detection)
    const METADATA_PROGRAM_ID_STR = 'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s';
    
    // Check if this is actually a metadata account, not a mint
    if (ownerBase58 === METADATA_PROGRAM_ID_STR) {
      console.error('❌ This appears to be a metadata account, not a mint address!');
      return { program: 'unknown', mintExists: false, owner: ownerBase58 };
    }
    
    // Compare using base58 strings (most reliable)
    if (ownerBase58 === SPL_TOKEN_PROGRAM_ID_STR) {
      return { program: 'spl-token', mintExists: true, owner: ownerBase58 };
    }
    
    if (ownerBase58 === TOKEN_2022_PROGRAM_ID_STR) {
      return { program: 'token-2022', mintExists: true, owner: ownerBase58 };
    }
    
    // Also check using PublicKey.equals() as fallback
    try {
      if (accountInfo.owner.equals(TOKEN_PROGRAM_ID)) {
        return { program: 'spl-token', mintExists: true, owner: ownerBase58 };
      }
      if (accountInfo.owner.equals(TOKEN_2022_PROGRAM_ID)) {
        return { program: 'token-2022', mintExists: true, owner: ownerBase58 };
      }
    } catch (e) {
      // Fallback comparison failed, continue with base58 result
    }
    
    // Unknown program - log for debugging
    console.warn('⚠️ Unknown mint owner:', ownerBase58);
    return { program: 'unknown', mintExists: true, owner: ownerBase58 };
    
  } catch (error) {
    console.error('Error detecting token program:', error);
    return { program: 'unknown', mintExists: false };
  }
}

/**
 * Enhanced token standard detection with fallback for metadata-less tokens
 */
export async function getTokenStandardEnhanced(
  connection: Connection,
  mint: PublicKey
): Promise<{ standard: string; reason: string; supply?: bigint; decimals?: number }> {
  try {
    // Try metadata first
    const METADATA_PROGRAM_ID = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');
    const [metadataPDA] = PublicKey.findProgramAddressSync(
      [
        Buffer.from('metadata'),
        METADATA_PROGRAM_ID.toBuffer(),
        mint.toBuffer(),
      ],
      METADATA_PROGRAM_ID
    );

    const accountInfo = await connection.getAccountInfo(metadataPDA);
    
    if (accountInfo) {
      // Has metadata - use existing logic
      const data = accountInfo.data;
      if (data.length > 679) {
        return { standard: 'ProgrammableNonFungible', reason: 'Has metadata, programmable' };
      }
      return { standard: 'NonFungible', reason: 'Has metadata, standard NFT' };
    }
    
    // No metadata - check mint info
    const mintInfo = await getMintInfo(connection, mint);
    
    if (!mintInfo || !mintInfo.exists) {
      return { standard: 'Unknown', reason: 'Mint does not exist' };
    }
    
    // Classify based on decimals and supply
    if (mintInfo.decimals === 0 && mintInfo.supply === BigInt(1)) {
      return { 
        standard: 'NonFungibleLegacy', 
        reason: 'No metadata, but decimals=0 and supply=1 (legacy 1/1)',
        supply: mintInfo.supply,
        decimals: mintInfo.decimals
      };
    }
    
    if (mintInfo.supply && mintInfo.supply > BigInt(1)) {
      return { 
        standard: 'SFT', 
        reason: `No metadata, supply=${mintInfo.supply.toString()} (semi-fungible, will list quantity=1)`,
        supply: mintInfo.supply,
        decimals: mintInfo.decimals
      };
    }
    
    return { standard: 'Unknown', reason: 'No metadata, cannot classify', supply: mintInfo.supply, decimals: mintInfo.decimals };
    
  } catch (error) {
    console.error('Error detecting token standard:', error);
    return { standard: 'Unknown', reason: `Error: ${error instanceof Error ? error.message : 'Unknown'}` };
  }
}

/**
 * Comprehensive token classification for listing
 */
export async function classifyTokenForListing(
  connection: Connection,
  mint: PublicKey,
  currentCluster?: 'mainnet-beta' | 'devnet'
): Promise<{
  program: 'spl-token' | 'token-2022' | 'unknown';
  standard: string;
  reason: string;
  mintExists: boolean;
  owner?: string;
  supply?: bigint;
  decimals?: number;
  detectedCluster?: 'mainnet-beta' | 'devnet' | null;
}> {
  try {
    // Detect program directly from chain (most reliable)
    const programDetection = await detectTokenProgramFromMint(connection, mint);
    
    if (!programDetection.mintExists) {
      // Mint not found on current cluster - try to detect which cluster it's on
      console.log('🔍 Mint not found on current cluster, detecting...');
      const clusterDetection = await detectClusterForMint(mint, currentCluster);
      
      // If found on a DIFFERENT cluster, return with that cluster
      if (clusterDetection.found && clusterDetection.cluster !== currentCluster) {
        return {
          program: 'unknown',
          standard: 'Unknown',
          reason: `Mint found on ${clusterDetection.cluster} (you are on ${currentCluster || 'unknown'})`,
          mintExists: false,
          detectedCluster: clusterDetection.cluster,
        };
      }
      
      // If found on the SAME cluster, something is wrong - treat as not found
      if (clusterDetection.found && clusterDetection.cluster === currentCluster) {
        console.warn('⚠️ Cluster detection found mint on same cluster but initial detection failed. Retrying...');
        // This shouldn't happen - retry the detection
        const retryDetection = await detectTokenProgramFromMint(connection, mint);
        if (retryDetection.mintExists) {
          // Use the retry result
          const { standard, reason, supply, decimals } = await getTokenStandardEnhanced(connection, mint);
          return {
            program: retryDetection.program,
            standard,
            reason,
            mintExists: true,
            owner: retryDetection.owner,
            supply,
            decimals,
            detectedCluster: currentCluster,
          };
        }
      }
      
      // Not found anywhere
      return {
        program: 'unknown',
        standard: 'Unknown',
        reason: 'Mint not found on any supported cluster',
        mintExists: false,
        detectedCluster: null,
      };
    }
    
    // Get standard with mint properties
    const { standard, reason, supply, decimals } = await getTokenStandardEnhanced(connection, mint);
    
    // If program is still unknown but mint exists, log for debugging
    if (programDetection.program === 'unknown' && programDetection.mintExists) {
      console.error('❌ Could not detect token program. Mint owner:', programDetection.owner);
    }
    
    return {
      program: programDetection.program,
      standard,
      reason,
      mintExists: true,
      owner: programDetection.owner,
      supply,
      decimals,
      detectedCluster: currentCluster,
    };
  } catch (error) {
    console.error('Error classifying token:', error);
    return {
      program: 'unknown',
      standard: 'Unknown',
      reason: `Error: ${error instanceof Error ? error.message : 'Unknown'}`,
      mintExists: false,
    };
  }
}

/**
 * Basic ownership verification without requiring metadata
 * Includes try-both fallback for program detection
 */
export async function verifyOwnershipBasic(
  connection: Connection,
  mint: PublicKey,
  owner: PublicKey,
  program: 'spl-token' | 'token-2022' | 'unknown'
): Promise<{ owned: boolean; amount: number; ata: PublicKey; reason?: string; programUsed?: string }> {
  try {
    // Always try both programs to ensure we find the token account
    const programsToTry: Array<{ id: typeof TOKEN_PROGRAM_ID | typeof TOKEN_2022_PROGRAM_ID; name: 'spl-token' | 'token-2022' }> = [];
    
    // If program is known, try it first
    if (program === 'spl-token') {
      programsToTry.push({ id: TOKEN_PROGRAM_ID, name: 'spl-token' });
      programsToTry.push({ id: TOKEN_2022_PROGRAM_ID, name: 'token-2022' });
    } else if (program === 'token-2022') {
      programsToTry.push({ id: TOKEN_2022_PROGRAM_ID, name: 'token-2022' });
      programsToTry.push({ id: TOKEN_PROGRAM_ID, name: 'spl-token' });
    } else {
      // Unknown - try both
      programsToTry.push({ id: TOKEN_PROGRAM_ID, name: 'spl-token' });
      programsToTry.push({ id: TOKEN_2022_PROGRAM_ID, name: 'token-2022' });
    }
    
    console.log(`🔍 Checking ownership for both token programs (detected: ${program})...`);
    
    // Try each program
    for (const prog of programsToTry) {
      try {
        const ata = await getAssociatedTokenAddress(mint, owner, true, prog.id);
        console.log(`🔍 Checking ${prog.name} ATA: ${ata.toBase58()}`);
        
        // Try to get the account
        const account = await getAccount(connection, ata, 'confirmed', prog.id);
        const balance = Number(account.amount);
        
        console.log(`✅ ${prog.name} account found with balance: ${balance}`);
        
        if (balance >= 1) {
          return {
            owned: true,
            amount: balance,
            ata,
            programUsed: prog.name,
            reason: `Verified via ${prog.name}`,
          };
        } else {
          console.warn(`⚠️ ${prog.name} account exists but balance is 0`);
        }
      } catch (error: any) {
        // Account doesn't exist or error reading it
        if (error.message?.includes('could not find account') || 
            error.message?.includes('Invalid param: could not find account')) {
          console.log(`ℹ️ No ${prog.name} account found for this mint`);
        } else {
          console.warn(`⚠️ ${prog.name} check error:`, error.message);
        }
      }
    }
    
    // Neither program found a token account with balance
    return {
      owned: false,
      amount: 0,
      ata: PublicKey.default,
      reason: 'No token account found with balance >= 1 for either SPL Token or Token-2022 programs',
    };
    
  } catch (error) {
    console.error('Error verifying ownership:', error);
    return {
      owned: false,
      amount: 0,
      ata: PublicKey.default,
      reason: `Error: ${error instanceof Error ? error.message : 'Unknown'}`,
    };
  }
}

/**
 * More reliable token standard detection using Helius DAS API
 * NOTE: This function is deprecated for client-side use. 
 * Use getTokenStandard with regular RPC instead.
 */
export async function getTokenStandardHelius(
  mint: string,
  heliusApiKey?: string
): Promise<string> {
  try {
    // Client-side should NEVER use Helius - return Unknown immediately
    if (typeof window !== 'undefined') {
      console.warn('Helius API should not be used client-side, returning Unknown');
      return 'Unknown';
    }
    
    const apiKey = heliusApiKey || import.meta.env.VITE_HELIUS_API_KEY;
    if (!apiKey) {
      console.warn('Helius API key not found, falling back to basic detection');
      return 'Unknown';
    }

    const url = `https://devnet.helius-rpc.com/?api-key=${apiKey}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 'token-standard-check',
        method: 'getAsset',
        params: { id: mint }
      })
    });

    const { result } = await response.json();
    
    if (result?.content?.metadata?.token_standard) {
      return result.content.metadata.token_standard;
    }

    // Fallback to checking interface
    if (result?.interface === 'ProgrammableNFT') {
      return 'ProgrammableNonFungible';
    }

    return 'NonFungible';
  } catch (error) {
    console.error('Helius token standard detection failed:', error);
    return 'Unknown';
  }
}

/**
 * Build an unsigned transaction to approve delegate authority
 * Seller must sign this transaction to authorize the platform to transfer their NFT
 */
export async function approveDelegateTx({
  connection,
  seller,
  mint,
  delegate
}: {
  connection: Connection;
  seller: PublicKey;
  mint: PublicKey;
  delegate: PublicKey;
}): Promise<Transaction> {
  const ata = await getAssociatedTokenAddress(mint, seller, true);

  // Verify the seller owns this token
  try {
    const tokenAccount = await getAccount(connection, ata);
    if (tokenAccount.amount < 1) {
      throw new Error('Seller does not own this NFT');
    }
  } catch (error) {
    throw new Error(`Failed to verify NFT ownership: ${error.message}`);
  }

  // Create approve instruction for amount = 1 (NFT)
  const approveIx = createApproveInstruction(
    ata,
    delegate,
    seller,
    1, // Amount (1 for NFTs)
    [],
    TOKEN_PROGRAM_ID
  );

  // Get latest blockhash
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

  // Create transaction
  const tx = new Transaction({
    feePayer: seller,
    blockhash,
    lastValidBlockHeight
  });
  
  tx.add(approveIx);

  return tx;
}

/**
 * Build an unsigned transaction to revoke delegate authority
 * Seller signs this to cancel their listing and revoke platform access
 */
export async function revokeDelegateTx({
  connection,
  seller,
  mint
}: {
  connection: Connection;
  seller: PublicKey;
  mint: PublicKey;
}): Promise<Transaction> {
  const ata = await getAssociatedTokenAddress(mint, seller, true);

  // Create revoke instruction
  const revokeIx = createRevokeInstruction(
    ata,
    seller,
    [],
    TOKEN_PROGRAM_ID
  );

  // Get latest blockhash
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

  // Create transaction
  const tx = new Transaction({
    feePayer: seller,
    blockhash,
    lastValidBlockHeight
  });
  
  tx.add(revokeIx);

  return tx;
}

/**
 * Execute a transfer using delegate authority (platform-side)
 * This should ONLY be called server-side with the platform keypair
 * 
 * @param delegateKeypair - The platform wallet keypair (KEEP SECRET!)
 */
export async function delegateTransfer({
  connection,
  delegateKeypair,
  mint,
  fromSeller,
  toBuyer
}: {
  connection: Connection;
  delegateKeypair: Keypair;
  mint: PublicKey;
  fromSeller: PublicKey;
  toBuyer: PublicKey;
}): Promise<string> {
  console.log('🔄 Executing delegate transfer...');
  console.log('Mint:', mint.toString());
  console.log('From:', fromSeller.toString());
  console.log('To:', toBuyer.toString());
  console.log('Delegate:', delegateKeypair.publicKey.toString());

  // Get seller's ATA
  const fromAta = await getAssociatedTokenAddress(mint, fromSeller, true);

  // Get or create buyer's ATA
  const { ata: toAta, instruction: createAtaIx } = await getOrCreateAta(
    connection,
    toBuyer,
    mint,
    delegateKeypair.publicKey
  );

  // Verify delegate authority
  try {
    const tokenAccount = await getAccount(connection, fromAta);
    
    if (tokenAccount.amount < 1) {
      throw new Error('Seller no longer owns this NFT');
    }

    if (!tokenAccount.delegate?.equals(delegateKeypair.publicKey)) {
      throw new Error('Platform wallet is not the approved delegate');
    }

    if (tokenAccount.delegatedAmount < 1) {
      throw new Error('Insufficient delegated amount');
    }

    console.log('✅ Delegate authority verified');
  } catch (error) {
    throw new Error(`Delegate verification failed: ${error.message}`);
  }

  // Build transfer transaction
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

  const tx = new Transaction({
    feePayer: delegateKeypair.publicKey,
    blockhash,
    lastValidBlockHeight
  });

  // Add create ATA instruction if needed
  if (createAtaIx) {
    console.log('Creating buyer ATA...');
    tx.add(createAtaIx);
  }

  // Add transfer instruction (using delegate authority)
  const transferIx = createTransferInstruction(
    fromAta,
    toAta,
    delegateKeypair.publicKey, // Authority is the delegate
    1, // Amount (1 for NFT)
    [],
    TOKEN_PROGRAM_ID
  );

  tx.add(transferIx);

  // Sign and send
  tx.partialSign(delegateKeypair);
  const rawTx = tx.serialize();

  console.log('📤 Sending delegate transfer transaction...');
  const signature = await connection.sendRawTransaction(rawTx, {
    skipPreflight: false,
    preflightCommitment: 'confirmed'
  });

  console.log('⏳ Confirming transaction...');
  await connection.confirmTransaction({
    signature,
    blockhash,
    lastValidBlockHeight
  }, 'confirmed');

  console.log('✅ Delegate transfer completed:', signature);
  return signature;
}

/**
 * Verify that a token account has an active delegate
 */
export async function verifyDelegateActive(
  connection: Connection,
  mint: PublicKey,
  owner: PublicKey,
  expectedDelegate: PublicKey
): Promise<boolean> {
  try {
    const ata = await getAssociatedTokenAddress(mint, owner, true);
    const tokenAccount = await getAccount(connection, ata);

    if (!tokenAccount.delegate) {
      return false;
    }

    if (!tokenAccount.delegate.equals(expectedDelegate)) {
      return false;
    }

    if (tokenAccount.delegatedAmount < 1) {
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error verifying delegate:', error);
    return false;
  }
}

/**
 * Get the explorer URL for a transaction
 */
export function getExplorerUrl(signature: string, network: 'mainnet' | 'devnet' = 'mainnet'): string {
  const cluster = network === 'mainnet' ? '' : `?cluster=${network}`;
  return `https://explorer.solana.com/tx/${signature}${cluster}`;
}

/**
 * Token Program Detection and Multi-Program Support
 */

export type TokenProgramKind = 'spl-token' | 'token-2022';

/**
 * Detect which token program owns a mint account
 */
export async function detectTokenProgram(
  connection: Connection,
  mint: PublicKey
): Promise<TokenProgramKind> {
  const ai = await connection.getAccountInfo(mint);
  if (!ai) throw new Error('Mint not found on-chain');
  return ai.owner.equals(TOKEN_2022_PROGRAM_ID) ? 'token-2022' : 'spl-token';
}

/**
 * Check if an NFT is a Programmable NFT (pNFT)
 * 
 * Note: Requires @metaplex-foundation/mpl-token-metadata to be installed
 * If not available, this function will return false
 */
export async function isProgrammableNft(
  connection: Connection,
  mint: PublicKey
): Promise<boolean> {
  try {
    // Try to use Metaplex metadata if available
    // This is a simplified check - in production you'd import from @metaplex-foundation/mpl-token-metadata
    const TMETA_PROGRAM_ID = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');
    const [metadataPda] = PublicKey.findProgramAddressSync(
      [Buffer.from('metadata'), TMETA_PROGRAM_ID.toBuffer(), mint.toBuffer()],
      TMETA_PROGRAM_ID
    );
    
    const accountInfo = await connection.getAccountInfo(metadataPda);
    if (!accountInfo) return false;
    
    // Check if TokenStandard field indicates ProgrammableNonFungible
    // This is a heuristic - proper implementation would deserialize the metadata
    const data = accountInfo.data;
    if (data.length > 679) {
      // Longer metadata accounts often indicate programmable NFTs
      return true;
    }
    
    return false;
  } catch (error) {
    console.warn('Error checking if programmable NFT:', error);
    return false;
  }
}

/**
 * Derive Associated Token Account for either SPL Token or Token-2022
 */
export function deriveAta(
  owner: PublicKey,
  mint: PublicKey,
  program: TokenProgramKind
): PublicKey {
  return program === 'token-2022'
    ? getATA22(mint, owner, false, TOKEN_2022_PROGRAM_ID)
    : getATA(mint, owner, false, TOKEN_PROGRAM_ID);
}

/**
 * Get mint decimals for either SPL Token or Token-2022
 */
export async function getMintDecimals(
  connection: Connection,
  mint: PublicKey,
  program: TokenProgramKind
): Promise<number> {
  const mintAcc = program === 'token-2022'
    ? await getMint22(connection, mint, 'confirmed', TOKEN_2022_PROGRAM_ID)
    : await getMintClassic(connection, mint, 'confirmed', TOKEN_PROGRAM_ID);
  return mintAcc.decimals;
}

/**
 * Build an approve delegate instruction for the correct token program
 */
export function buildApproveDelegateIx(params: {
  ata: PublicKey;
  owner: PublicKey;
  delegate: PublicKey;
  program: TokenProgramKind;
}) {
  const { ata, owner, delegate, program } = params;
  const amount = BigInt(1); // For NFTs, always 1
  
  return program === 'token-2022'
    ? createApprove22(ata, delegate, owner, amount, [], TOKEN_2022_PROGRAM_ID)
    : createApproveClassic(ata, delegate, owner, Number(amount), [], TOKEN_PROGRAM_ID);
}

/**
 * Verify delegate authorization on-chain (trust blockchain over database)
 * Checks if the platform delegate has authority to transfer the NFT
 */
export async function assertOnChainDelegateActive(params: {
  connection: Connection;
  mint: PublicKey;
  seller: PublicKey;
  platformDelegate: PublicKey;
  program: TokenProgramKind;
}): Promise<{
  active: boolean;
  ata: string;
  delegate: string | null;
  delegatedAmount: number;
  reason?: string;
}> {
  const { connection, mint, seller, platformDelegate, program } = params;
  const ataPk = deriveAta(seller, mint, program);

  try {
    const acct =
      program === 'token-2022'
        ? await get22Account(connection, ataPk, 'confirmed', TOKEN_2022_PROGRAM_ID)
        : await getClassicAccount(connection, ataPk, 'confirmed', TOKEN_PROGRAM_ID);

    const amount = Number(acct.amount);
    if (amount !== 1) {
      return { 
        active: false, 
        ata: ataPk.toBase58(), 
        delegate: null, 
        delegatedAmount: 0, 
        reason: 'NFT not in seller ATA' 
      };
    }

    const delegate = (acct as any).delegate ?? null;
    const delegatedAmount = Number((acct as any).delegatedAmount ?? 0);
    const isActive =
      delegate?.toBase58?.() === platformDelegate.toBase58() &&
      delegatedAmount === 1;

    return {
      active: isActive,
      ata: ataPk.toBase58(),
      delegate: delegate ? delegate.toBase58() : null,
      delegatedAmount,
      reason: isActive ? undefined : 'delegate mismatch or delegatedAmount != 1',
    };
  } catch (error) {
    console.error('Error checking on-chain delegate:', error);
    return {
      active: false,
      ata: ataPk.toBase58(),
      delegate: null,
      delegatedAmount: 0,
      reason: `Failed to fetch ATA: ${error.message}`,
    };
  }
}

